// src/lib/questions.ts
// Complete implementation-ready 26-question bank

export interface NumberOptions {
  min: number
  max: number
  unit: string
  optional?: boolean
  placeholder?: string
  hint?: string
}

export interface LocationEntry {
  key: string
  label: string
}

export interface LocationOptions {
  featured: LocationEntry[]
  counties: LocationEntry[]
  countries: LocationEntry[]
}

export interface QuestionDefinition {
  id: string
  section: 'A' | 'B' | 'C' | 'D' | 'E'
  sectionIndex: number
  text: string
  type: 'single' | 'multi' | 'number' | 'location'
  options: OptionDefinition[]
  paintDomains: string[]
  numberOptions?: NumberOptions
  locationOptions?: LocationOptions
}

export interface OptionDefinition {
  key: string
  label: string
  score: number
}

export interface Answer {
  questionId: string
  answerKey: string       // For multi-select, comma-separated keys
  answerLabel: string     // For multi-select, comma-separated labels
}

export const SECTION_INTROS: Record<string, { title: string; body: string }> = {
  A: {
    title: 'Your Cycle',
    body: "Let's start with your cycle — the basics that most health conversations skip past.",
  },
  B: {
    title: 'Your Symptoms',
    body: "These next questions are about symptoms you may have learned to live with. We'd like to hear about them — because in FEMM medicine, symptoms are not inconveniences. They are information.",
  },
  C: {
    title: 'Your Mood & Wellbeing',
    body: "This section is not about mental health as a separate topic. It is about your hormones — because the same hormones that regulate your cycle regulate your brain. Research published in 2024 found that women with hormonal cycle disruption had significantly higher rates of tension, fatigue, and depression than women with healthy ovulatory cycles. These are not character traits. They are downstream effects.",
  },
  D: {
    title: 'Medical History & Investigations',
    body: "These questions help us understand what has — and hasn't — already been looked at. There are no wrong answers here.",
  },
  E: {
    title: 'Almost Done',
    body: "Just a few questions to help us personalise your results.",
  },
}

export const QUESTIONS: QuestionDefinition[] = [
  // ═══ SECTION A — Your Cycle ════════════════════════
  {
    id: 'Q1', section: 'A', sectionIndex: 0,
    text: 'From the first day of one period to the first day of the next, how long is your typical cycle?',
    type: 'single',
    paintDomains: ['A', 'cycle'],
    options: [
      { key: 'short',     label: 'Less than 21 days',           score: 2 },
      { key: 'normal',    label: '22–35 days',                   score: 0 },
      { key: 'long',      label: '36–45 days',                   score: 2 },
      { key: 'very-long', label: 'More than 45 days',            score: 3 },
      { key: 'varies',    label: "It varies — I can't predict it", score: 3 },
    ],
  },
  {
    id: 'Q2', section: 'A', sectionIndex: 1,
    text: 'How many days does your period typically last?',
    type: 'single',
    paintDomains: ['T', 'cycle'],
    options: [
      { key: 'very-short', label: '1–2 days',         score: 2 },
      { key: 'normal',     label: '3–5 days',          score: 0 },
      { key: 'long',       label: '6–7 days',          score: 1 },
      { key: 'very-long',  label: 'More than 7 days',  score: 2 },
    ],
  },
  {
    id: 'Q3', section: 'A', sectionIndex: 2,
    text: 'How would you describe the volume and quality of your menstrual flow?',
    type: 'single',
    paintDomains: ['T', 'cycle'],
    options: [
      { key: 'very-light', label: 'Very light — barely there, or mainly spotting',                    score: 2 },
      { key: 'normal',     label: 'Light to moderate — normal but manageable',                         score: 0 },
      { key: 'heavy',      label: 'Heavy — I need to change protection frequently',                    score: 1 },
      { key: 'extreme',    label: 'Extremely heavy — soaking through protection, passing clots',       score: 3 },
    ],
  },
  {
    id: 'Q4', section: 'A', sectionIndex: 3,
    text: 'How painful are your periods?',
    type: 'single',
    paintDomains: ['cycle'],
    options: [
      { key: 'none',         label: 'No significant pain',                                               score: 0 },
      { key: 'mild',         label: 'Mild — some discomfort, no impact on my day',                      score: 0 },
      { key: 'moderate',     label: 'Moderate — I manage with painkillers and adjust my activities',    score: 1 },
      { key: 'severe',       label: 'Severe — I am incapacitated for 1–3 days each month',             score: 2 },
      { key: 'debilitating', label: 'Debilitating — pain affects my ability to work, move, or function', score: 3 },
    ],
  },
  {
    id: 'Q5', section: 'A', sectionIndex: 4,
    text: 'Have you ever had your period stop for three or more consecutive months (without being pregnant or breastfeeding)?',
    type: 'single',
    paintDomains: ['P', 'cycle'],
    options: [
      { key: 'never',         label: 'No, never',                                         score: 0 },
      { key: 'once',          label: 'Yes, once — and it returned on its own',             score: 1 },
      { key: 'multiple',      label: 'Yes, and it has happened more than once',            score: 3 },
      { key: 'absent-6m-plus', label: 'My period has been absent for more than 6 months right now', score: 3 },
    ],
  },
  {
    id: 'Q6', section: 'A', sectionIndex: 5,
    text: 'Do you know whether you ovulate regularly each cycle?',
    type: 'single',
    paintDomains: ['cycle'],
    options: [
      { key: 'confirmed', label: 'Yes — I track it and confirm it',                       score: 0 },
      { key: 'assume',    label: 'I assume I do because my periods come',                  score: 2 },
      { key: 'unsure',    label: "I'm not sure / I've never thought about it",             score: 2 },
      { key: 'no',        label: "No — I've been told or suspect I may not be ovulating",  score: 3 },
    ],
  },
  {
    id: 'Q27', section: 'A', sectionIndex: 6,
    text: 'Optional: how much does your cycle length typically vary from month to month?',
    type: 'number',
    paintDomains: [],
    options: [],
    numberOptions: {
      min: 0,
      max: 60,
      unit: 'days',
      optional: true,
      placeholder: 'e.g. 5',
      hint: 'If your cycle is sometimes 26 days and sometimes 32 days, enter 6. Leave blank if unsure.',
    },
  },

  // ═══ SECTION B — Your Symptoms ══════════════════════
  {
    id: 'Q7', section: 'B', sectionIndex: 0,
    text: 'Do you experience breakouts or acne? If so, where do they tend to appear most?',
    type: 'single',
    paintDomains: ['A'],
    options: [
      { key: 'none',       label: "I don't really get acne",                     score: 0 },
      { key: 'forehead',   label: 'Mostly on my forehead or nose',               score: 0 },
      { key: 'jaw',        label: 'Mostly on my jaw, chin, or neck',             score: 3 },
      { key: 'cheeks',     label: 'Across my cheeks',                            score: 1 },
      { key: 'widespread', label: 'All over my face and back/chest',             score: 2 },
      { key: 'cyclical',   label: 'It comes and goes around my period',          score: 1 },
    ],
  },
  {
    id: 'Q8', section: 'B', sectionIndex: 1,
    text: "Have you noticed hair growing in places you don't want it — for example, on your face, chin, chest, stomach, or inner thighs?",
    type: 'single',
    paintDomains: ['A'],
    options: [
      { key: 'no',          label: 'No',                                               score: 0 },
      { key: 'mild',        label: 'A little — a few hairs I manage',                 score: 1 },
      { key: 'moderate',    label: 'Yes — noticeable and increasing',                 score: 2 },
      { key: 'significant', label: 'Yes — significant, affecting my confidence',      score: 3 },
    ],
  },
  {
    id: 'Q9', section: 'B', sectionIndex: 2,
    text: 'Have you noticed thinning of the hair on your scalp, or hair that seems to be shedding more than usual?',
    type: 'single',
    paintDomains: ['A', 'T'],
    options: [
      { key: 'no',          label: 'No change',                                                  score: 0 },
      { key: 'mild',        label: 'A little more shedding than usual',                          score: 1 },
      { key: 'thinning',    label: 'Noticeable thinning — especially at the parting or crown',   score: 2 },
      { key: 'significant', label: 'Significant loss — affecting how my hair looks and feels',   score: 3 },
    ],
  },
  {
    id: 'Q10', section: 'B', sectionIndex: 3,
    text: 'Have you gained weight in the last year or two, despite no major changes to your diet or exercise habits? If so, where do you tend to carry it?',
    type: 'single',
    paintDomains: ['I'],
    options: [
      { key: 'stable',    label: 'No — my weight is stable',                                  score: 0 },
      { key: 'lifestyle', label: 'Slight gain, but I can attribute it to lifestyle',           score: 0 },
      { key: 'belly',     label: 'Yes — gained weight, mainly around my belly and waist',     score: 3 },
      { key: 'even',      label: 'Yes — gained weight more evenly across my body',            score: 2 },
      { key: 'hips',      label: 'Yes — gained weight mainly on hips and thighs',            score: 1 },
      { key: 'cant-lose', label: 'I have struggled to lose weight no matter what I try',     score: 2 },
    ],
  },
  {
    id: 'Q11', section: 'B', sectionIndex: 4,
    text: 'Have you ever noticed a milky or clear discharge from your nipples when you are not pregnant or breastfeeding?',
    type: 'single',
    paintDomains: ['P'],
    options: [
      { key: 'no',          label: 'No',                                               score: 0 },
      { key: 'once-twice',  label: 'Once or twice — I dismissed it',                  score: 2 },
      { key: 'occasional',  label: 'Yes — it happens occasionally',                   score: 3 },
      { key: 'regularly',   label: 'Yes — it happens regularly or with pressure',     score: 3 },
    ],
  },
  {
    id: 'Q12', section: 'B', sectionIndex: 5,
    text: 'How would you honestly describe your energy levels on most days?',
    type: 'single',
    paintDomains: ['T', 'I'],
    options: [
      { key: 'good',            label: 'Consistently good — I feel energetic most of the day',        score: 0 },
      { key: 'variable',        label: 'Variable — some good days, some exhausted days',              score: 1 },
      { key: 'low',             label: 'Generally low — I rarely feel rested or energised',           score: 2 },
      { key: 'depleted',        label: 'Severely depleted — fatigue affects my ability to function',  score: 3 },
      { key: 'afternoon-crash', label: 'My energy crashes sharply in the afternoons',                 score: 2 },
    ],
  },
  {
    id: 'Q13', section: 'B', sectionIndex: 6,
    text: 'Do you experience strong cravings for sugar, bread, or carbohydrate-rich foods — especially in the week before your period, or in the afternoon?',
    type: 'single',
    paintDomains: ['I'],
    options: [
      { key: 'no',         label: 'No — cravings are not a significant issue for me',           score: 0 },
      { key: 'mild',       label: "Mild — I have cravings but they're manageable",              score: 1 },
      { key: 'pre-period', label: 'Yes — pre-period cravings are intense and hard to ignore',   score: 2 },
      { key: 'daily',      label: 'Yes — I crave sweets or carbs in the afternoons almost daily', score: 3 },
      { key: 'compulsive', label: 'Yes — cravings feel compulsive and are affecting my diet',   score: 3 },
    ],
  },
  {
    id: 'Q14', section: 'B', sectionIndex: 7,
    text: 'Compared to how you felt 2–3 years ago, how has your interest in sex changed?',
    type: 'single',
    paintDomains: ['P'],
    options: [
      { key: 'same',               label: 'No change — still engaged and interested',                    score: 0 },
      { key: 'slightly-lower',     label: 'Slightly lower — but understandable given life circumstances', score: 1 },
      { key: 'noticeably-lower',   label: "Noticeably lower — it's a pattern, not just occasional",       score: 2 },
      { key: 'significantly-reduced', label: 'Significantly reduced — I have little or no interest',     score: 3 },
      { key: 'never-present',      label: 'It was never particularly present',                            score: 1 },
    ],
  },

  // ═══ SECTION C — Mood & Wellbeing ════════════════════
  {
    id: 'Q15', section: 'C', sectionIndex: 0,
    text: 'In the week before your period, how significant is the shift in your mood, energy, or emotional state?',
    type: 'single',
    paintDomains: ['mood'],
    options: [
      { key: 'none',     label: 'None — I feel roughly the same throughout my cycle',                       score: 0 },
      { key: 'mild',     label: 'Mild — slightly more irritable or tired, but manageable',                  score: 0 },
      { key: 'moderate', label: 'Moderate — noticeable shift; affects my relationships or work',            score: 2 },
      { key: 'severe',   label: 'Severe — I become a noticeably different person',                          score: 3 },
      { key: 'extreme',  label: 'Extreme — the week before my period is the hardest week of my month, every month', score: 3 },
    ],
  },
  {
    id: 'Q16', section: 'C', sectionIndex: 1,
    text: 'In general, how would you describe your emotional and psychological wellbeing over the past three months?',
    type: 'single',
    paintDomains: ['T', 'mood'],
    options: [
      { key: 'good',                 label: 'Good — I feel emotionally well most of the time',                                             score: 0 },
      { key: 'some-difficulty',      label: 'Some difficult days, but mostly managing',                                                    score: 1 },
      { key: 'frequently-struggling', label: 'Frequently anxious, irritable, or low — this is my baseline now',                          score: 2 },
      { key: 'struggling',           label: 'Persistently struggling — depression, anxiety, or emotional numbness are regular features',  score: 3 },
    ],
  },
  {
    id: 'Q17', section: 'C', sectionIndex: 2,
    text: 'Do you regularly experience any of the following? (Select all that apply)',
    type: 'multi',
    paintDomains: ['mood'],
    options: [
      { key: 'brain-fog',   label: 'Brain fog or difficulty thinking clearly',       score: 2 },
      { key: 'memory',      label: 'Poor memory or forgetting things more than usual', score: 2 },
      { key: 'concentration', label: 'Difficulty concentrating on tasks',            score: 1 },
      { key: 'anxiety',     label: 'Anxiety or a sense of dread without clear cause', score: 2 },
      { key: 'low-mood',    label: 'Persistent low mood',                            score: 2 },
      { key: 'mood-swings', label: 'Mood swings unrelated to circumstances',         score: 2 },
      { key: 'none',        label: 'None of the above',                              score: 0 },
    ],
  },
  {
    id: 'Q18', section: 'C', sectionIndex: 3,
    text: 'Compared to two to three years ago, how has your sense of confidence and self-esteem changed?',
    type: 'single',
    paintDomains: ['A', 'I', 'mood'],
    options: [
      { key: 'same-better',       label: 'Same or better — I feel good about myself',                       score: 0 },
      { key: 'slightly-lower',    label: 'Slightly lower — but I can attribute it to life changes',         score: 0 },
      { key: 'noticeably-lower',  label: 'Noticeably lower — I feel less like myself',                      score: 2 },
      { key: 'significantly-lower', label: 'Significantly lower — my confidence has declined substantially', score: 3 },
      { key: 'inadequacy',        label: 'I struggle with a persistent sense of inadequacy',                 score: 3 },
    ],
  },
  {
    id: 'Q19', section: 'C', sectionIndex: 4,
    text: 'How would you describe the quality of your sleep?',
    type: 'single',
    paintDomains: ['T', 'mood'],
    options: [
      { key: 'good',           label: 'Good — I fall asleep easily and wake rested',       score: 0 },
      { key: 'variable',       label: 'Variable — some nights fine, others difficult',     score: 1 },
      { key: 'poor',           label: 'Poor — I struggle to fall asleep or wake frequently', score: 2 },
      { key: 'very-poor',      label: 'Very poor — I rarely feel rested',                  score: 3 },
      { key: 'wake-exhausted', label: 'I sleep but wake exhausted',                        score: 2 },
    ],
  },

  // ═══ SECTION D — Medical History ════════════════════
  {
    id: 'Q20', section: 'D', sectionIndex: 0,
    text: 'Have you ever been formally diagnosed with any of the following? (Select all that apply)',
    type: 'multi',
    paintDomains: [],
    options: [
      { key: 'pcos',              label: 'Polycystic Ovary Syndrome (PCOS)',                    score: 1 },
      { key: 'endometriosis',     label: 'Endometriosis',                                       score: 1 },
      { key: 'thyroid',           label: 'A thyroid condition (hypo- or hyperthyroid)',          score: 1 },
      { key: 'insulin-resistance', label: 'Insulin resistance or pre-diabetes',                 score: 1 },
      { key: 'none',              label: 'None of the above',                                   score: 0 },
      { key: 'unsure',            label: "I'm not sure / I was told something but don't remember", score: 2 },
    ],
  },
  {
    id: 'Q21', section: 'D', sectionIndex: 1,
    text: 'In the last two years, which of the following hormonal or metabolic tests have you had? (Select all that apply)',
    type: 'multi',
    paintDomains: [],
    options: [
      { key: 'fsh-lh',              label: 'FSH and/or LH',                                        score: 0 },
      { key: 'prolactin',           label: 'Prolactin',                                             score: 0 },
      { key: 'tsh-t4',              label: 'TSH and/or T4',                                         score: 0 },
      { key: 'testosterone-dheas',  label: 'Testosterone and/or DHEAS',                            score: 0 },
      { key: 'ogtt-i',              label: 'Insulin resistance / glucose tolerance test (OGTT)',    score: 0 },
      { key: 'progesterone',        label: 'Progesterone (day 21)',                                 score: 0 },
      { key: 'none',                label: 'None of the above',                                     score: 0 },
      { key: 'dont-know',           label: "I don't know what tests I've had",                      score: 0 },
    ],
  },
  {
    id: 'Q22', section: 'D', sectionIndex: 2,
    text: 'Are you currently using, or have you recently stopped, hormonal contraception (pill, injection, implant, hormonal IUD)?',
    type: 'single',
    paintDomains: [],
    options: [
      { key: 'none',          label: 'No — I have not used hormonal contraception',           score: 0 },
      { key: 'current',       label: 'Yes — I am currently on it',                            score: 1 },
      { key: 'stopped-lt-6m', label: 'Yes — I stopped less than 6 months ago',               score: 2 },
      { key: 'stopped-6-12m', label: 'Yes — I stopped 6–12 months ago',                      score: 1 },
      { key: 'stopped-gt-1y', label: 'Yes — I stopped more than a year ago',                 score: 0 },
    ],
  },
  {
    id: 'Q23', section: 'D', sectionIndex: 3,
    text: 'When you have raised these symptoms with a doctor, what has typically happened?',
    type: 'single',
    paintDomains: [],
    options: [
      { key: 'not-raised',       label: "I haven't raised them — I assumed they were normal",   score: 0 },
      { key: 'told-normal',      label: 'I was told they were normal / nothing was done',       score: 0 },
      { key: 'offered-pill',     label: 'I was offered the pill as a solution',                 score: 0 },
      { key: 'referred',         label: 'I was referred to a specialist',                       score: 0 },
      { key: 'investigated',     label: 'My concerns were taken seriously and investigated',    score: 0 },
      { key: 'told-lose-weight', label: 'I was told to lose weight / manage stress',           score: 0 },
    ],
  },

  // ═══ SECTION E — Context & Goals ═════════════════════
  {
    id: 'Q24', section: 'E', sectionIndex: 0,
    text: 'In relation to pregnancy, which of the following best describes where you are right now?',
    type: 'single',
    paintDomains: [],
    options: [
      { key: 'not-planning', label: 'I am not planning pregnancy now or in the near future',              score: 0 },
      { key: 'future',       label: 'I am not planning pregnancy now but want to in the future',           score: 0 },
      { key: 'ttc',          label: 'I am actively trying to conceive',                                    score: 0 },
      { key: 'ttc-6m-plus',  label: 'I have been trying to conceive for more than 6 months without success', score: 0 },
    ],
  },
  {
    id: 'Q25', section: 'E', sectionIndex: 1,
    text: 'How old are you?',
    type: 'single',
    paintDomains: [],
    options: [
      { key: 'under-18', label: 'Under 18', score: 0 },
      { key: '18-25',    label: '18–25',    score: 0 },
      { key: '26-35',    label: '26–35',    score: 0 },
      { key: '36-42',    label: '36–42',    score: 0 },
      { key: 'over-42',  label: 'Over 42',  score: 0 },
    ],
  },
  {
    id: 'Q26', section: 'E', sectionIndex: 2,
    text: 'Where are you based?',
    type: 'location',
    paintDomains: [],
    options: [],
    locationOptions: {
      featured: [
        { key: 'nairobi', label: 'Nairobi' },
        { key: 'mombasa', label: 'Mombasa' },
        { key: 'kisumu',  label: 'Kisumu'  },
      ],
      counties: [
        { key: 'baringo',         label: 'Baringo' },
        { key: 'bomet',           label: 'Bomet' },
        { key: 'bungoma',         label: 'Bungoma' },
        { key: 'busia',           label: 'Busia' },
        { key: 'elgeyo-marakwet', label: 'Elgeyo-Marakwet' },
        { key: 'embu',            label: 'Embu' },
        { key: 'garissa',         label: 'Garissa' },
        { key: 'homa-bay',        label: 'Homa Bay' },
        { key: 'isiolo',          label: 'Isiolo' },
        { key: 'kajiado',         label: 'Kajiado' },
        { key: 'kakamega',        label: 'Kakamega' },
        { key: 'kericho',         label: 'Kericho' },
        { key: 'kiambu',          label: 'Kiambu' },
        { key: 'kilifi',          label: 'Kilifi' },
        { key: 'kirinyaga',       label: 'Kirinyaga' },
        { key: 'kisii',           label: 'Kisii' },
        { key: 'kitui',           label: 'Kitui' },
        { key: 'kwale',           label: 'Kwale' },
        { key: 'laikipia',        label: 'Laikipia' },
        { key: 'lamu',            label: 'Lamu' },
        { key: 'machakos',        label: 'Machakos' },
        { key: 'makueni',         label: 'Makueni' },
        { key: 'mandera',         label: 'Mandera' },
        { key: 'marsabit',        label: 'Marsabit' },
        { key: 'meru',            label: 'Meru' },
        { key: 'migori',          label: 'Migori' },
        { key: 'muranga',         label: "Murang'a" },
        { key: 'nakuru',          label: 'Nakuru' },
        { key: 'nandi',           label: 'Nandi' },
        { key: 'narok',           label: 'Narok' },
        { key: 'nyandarua',       label: 'Nyandarua' },
        { key: 'nyamira',         label: 'Nyamira' },
        { key: 'nyeri',           label: 'Nyeri' },
        { key: 'samburu',         label: 'Samburu' },
        { key: 'siaya',           label: 'Siaya' },
        { key: 'taita-taveta',    label: 'Taita-Taveta' },
        { key: 'tana-river',      label: 'Tana River' },
        { key: 'tharaka-nithi',   label: 'Tharaka-Nithi' },
        { key: 'trans-nzoia',     label: 'Trans-Nzoia' },
        { key: 'turkana',         label: 'Turkana' },
        { key: 'uasin-gishu',     label: 'Uasin Gishu' },
        { key: 'vihiga',          label: 'Vihiga' },
        { key: 'wajir',           label: 'Wajir' },
        { key: 'west-pokot',      label: 'West Pokot' },
      ],
      countries: [
        // Africa
        { key: 'outside-botswana',     label: 'Botswana' },
        { key: 'outside-burundi',      label: 'Burundi' },
        { key: 'outside-cameroon',     label: 'Cameroon' },
        { key: 'outside-drc',          label: 'Congo (DRC)' },
        { key: 'outside-djibouti',     label: 'Djibouti' },
        { key: 'outside-egypt',        label: 'Egypt' },
        { key: 'outside-eritrea',      label: 'Eritrea' },
        { key: 'outside-eswatini',     label: 'Eswatini' },
        { key: 'outside-ethiopia',     label: 'Ethiopia' },
        { key: 'outside-ghana',        label: 'Ghana' },
        { key: 'outside-ivory-coast',  label: "Côte d'Ivoire" },
        { key: 'outside-lesotho',      label: 'Lesotho' },
        { key: 'outside-madagascar',   label: 'Madagascar' },
        { key: 'outside-malawi',       label: 'Malawi' },
        { key: 'outside-mauritius',    label: 'Mauritius' },
        { key: 'outside-morocco',      label: 'Morocco' },
        { key: 'outside-mozambique',   label: 'Mozambique' },
        { key: 'outside-namibia',      label: 'Namibia' },
        { key: 'outside-nigeria',      label: 'Nigeria' },
        { key: 'outside-rwanda',       label: 'Rwanda' },
        { key: 'outside-senegal',      label: 'Senegal' },
        { key: 'outside-seychelles',   label: 'Seychelles' },
        { key: 'outside-somalia',      label: 'Somalia' },
        { key: 'outside-south-africa', label: 'South Africa' },
        { key: 'outside-south-sudan',  label: 'South Sudan' },
        { key: 'outside-sudan',        label: 'Sudan' },
        { key: 'outside-tanzania',     label: 'Tanzania' },
        { key: 'outside-uganda',       label: 'Uganda' },
        { key: 'outside-zambia',       label: 'Zambia' },
        { key: 'outside-zimbabwe',     label: 'Zimbabwe' },
        // Middle East
        { key: 'outside-bahrain',      label: 'Bahrain' },
        { key: 'outside-jordan',       label: 'Jordan' },
        { key: 'outside-kuwait',       label: 'Kuwait' },
        { key: 'outside-oman',         label: 'Oman' },
        { key: 'outside-qatar',        label: 'Qatar' },
        { key: 'outside-saudi-arabia', label: 'Saudi Arabia' },
        { key: 'outside-uae',          label: 'United Arab Emirates' },
        // Europe
        { key: 'outside-austria',      label: 'Austria' },
        { key: 'outside-belgium',      label: 'Belgium' },
        { key: 'outside-denmark',      label: 'Denmark' },
        { key: 'outside-finland',      label: 'Finland' },
        { key: 'outside-france',       label: 'France' },
        { key: 'outside-germany',      label: 'Germany' },
        { key: 'outside-ireland',      label: 'Ireland' },
        { key: 'outside-italy',        label: 'Italy' },
        { key: 'outside-netherlands',  label: 'Netherlands' },
        { key: 'outside-norway',       label: 'Norway' },
        { key: 'outside-spain',        label: 'Spain' },
        { key: 'outside-sweden',       label: 'Sweden' },
        { key: 'outside-switzerland',  label: 'Switzerland' },
        { key: 'outside-uk',           label: 'United Kingdom' },
        // Americas
        { key: 'outside-canada',       label: 'Canada' },
        { key: 'outside-us',           label: 'United States' },
        { key: 'outside-brazil',       label: 'Brazil' },
        // Asia / Pacific
        { key: 'outside-australia',    label: 'Australia' },
        { key: 'outside-china',        label: 'China' },
        { key: 'outside-india',        label: 'India' },
        { key: 'outside-new-zealand',  label: 'New Zealand' },
        { key: 'outside-singapore',    label: 'Singapore' },
        // Catch-all
        { key: 'outside-other',        label: 'Other country' },
      ],
    },
  },
]
